/* Soul wishlist: saved product handles in localStorage (per device, no login needed).
   Hearts: [data-soul-wish="<handle>"] on product cards and in quick view. Count: [data-soul-wish-count].
   The wishlist page (<soul-wishlist>) loads each saved product from /products/<handle>.js. */
(() => {
  if (window.soulWishlist) return;
  const KEY = 'soulWishlist';
  const read = () => { try { const v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (err) { return []; } };
  const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 100))); } catch (err) {} };
  const has = (handle) => read().includes(handle);

  const paint = () => {
    const list = read();
    document.querySelectorAll('[data-soul-wish]').forEach((b) => {
      const on = list.includes(b.dataset.soulWish);
      b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    });
    document.querySelectorAll('[data-soul-wish-count]').forEach((c) => { c.textContent = list.length; c.hidden = list.length === 0; });
  };

  const toast = (saved) => {
    document.querySelector('.soul-toast')?.remove();
    const t = document.createElement('a'); t.className = 'soul-toast soul-toast--wish'; t.setAttribute('role', 'status');
    t.href = '/pages/wishlist';
    t.innerHTML = saved ? '<strong>♥ Saved to your wishlist</strong><span>Tap to view</span>' : '<strong>Removed from your wishlist</strong>';
    document.body.appendChild(t);
    requestAnimationFrame(() => requestAnimationFrame(() => t.classList.add('is-in')));
    setTimeout(() => { t.classList.remove('is-in'); setTimeout(() => t.remove(), 400); }, 2200);
  };

  const toggle = (handle) => {
    let list = read(); const on = list.includes(handle);
    list = on ? list.filter((h) => h !== handle) : [handle, ...list];
    write(list); paint(); toast(!on);
    document.dispatchEvent(new CustomEvent('soul:wishlist', { detail: { handle, saved: !on } }));
  };

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-soul-wish]'); if (!b) return;
    e.preventDefault(); e.stopPropagation(); toggle(b.dataset.soulWish);
  });
  // hearts arrive later too (recommendations, quick view, drawer re-renders)
  let queued = false;
  new MutationObserver(() => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; paint(); }); }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('storage', (e) => e.key === KEY && paint());
  window.addEventListener('pageshow', paint);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', paint) : paint();

  window.soulWishlist = { read, has, toggle };

  /* wishlist page */
  class SoulWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wish-grid]'); this.empty = this.querySelector('[data-wish-empty]');
      this.format = this.dataset.moneyFormat || '₹{{amount}}';
      this.render();
      document.addEventListener('soul:wishlist', (e) => { if (!e.detail.saved) this.querySelector(`[data-wish-item="${CSS.escape(e.detail.handle)}"]`)?.remove(); this.updateEmpty(); });
    }

    money(cents) {
      return this.format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, kind) => {
        const digits = /no_decimals/.test(kind) ? 0 : 2;
        return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
      });
    }

    updateEmpty() {
      const n = this.grid.children.length;
      this.empty.hidden = n > 0; this.grid.hidden = n === 0;
      const count = this.querySelector('[data-wish-total]'); if (count) count.textContent = n ? `${n} saved piece${n === 1 ? '' : 's'}` : '';
    }

    async render() {
      const handles = read();
      if (!handles.length) { this.updateEmpty(); return; }
      this.grid.innerHTML = handles.map(() => '<div class="soul-wish__card is-loading"><span></span></div>').join('');
      this.grid.hidden = false; this.empty.hidden = true;
      const products = await Promise.all(handles.map((h) => fetch(`/products/${encodeURIComponent(h)}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      const gone = handles.filter((h, i) => !products[i]);
      if (gone.length) write(read().filter((h) => !gone.includes(h))); // unpublished or deleted products drop out quietly
      this.grid.innerHTML = products.filter(Boolean).map((p) => this.card(p)).join('');
      this.updateEmpty(); paint();
    }

    card(p) {
      const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=500` : '';
      const off = p.compare_at_price > p.price ? Math.round(((p.compare_at_price - p.price) * 100) / p.compare_at_price) : 0;
      return `<div class="soul-wish__card" data-wish-item="${esc(p.handle)}">
        <a class="soul-wish__img" href="${esc(p.url)}">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" loading="lazy" width="500" height="500">` : ''}${p.available ? '' : '<span class="soul-wish__sold">Sold out</span>'}</a>
        <button type="button" class="soul-wish__remove" data-soul-wish="${esc(p.handle)}" aria-label="Remove from wishlist">×</button>
        <a class="soul-wish__name" href="${esc(p.url)}">${esc(p.title)}</a>
        <p class="soul-wish__price">${this.money(p.price)}${off ? ` <s>${this.money(p.compare_at_price)}</s> <span class="soul-off">${off}% off</span>` : ''}</p>
        <button type="button" class="soul-wish__add" data-soul-qv="${esc(p.url)}"${p.available ? '' : ' disabled'}>${p.available ? 'Add to cart' : 'Sold out'}</button>
      </div>`;
    }
  }
  customElements.get('soul-wishlist') || customElements.define('soul-wishlist', SoulWishlistPage);
})();
